import { Fragment, useState, useCallback, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import clsx from 'clsx'
import TimeAgo from 'javascript-time-ago'
import InfiniteScroll from 'react-infinite-scroll-component'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { IconSvg, AvatarImage, PieImage } from 'components/common'
import {
	NotificationType,
	UserTier,
	useGetNotificationsQuery,
	useReadAllNotificationMutation,
	useReadNotificationMutation,
} from 'graphql/generated/scheme'
import { useAuth } from 'hooks/useAuth'
import { useStore } from 'hooks/useStore'
import { Button } from 'components/atoms/button'
import {
	getCustomProperty,
	getNotificationEventAvatar,
	getNotificationNavigation,
	NotificationItem,
	NOTIFICATION_CONFIG,
} from 'utils/notification-helpers'
import { SeriesStackIcon } from 'pages/RepClubs/components/series-stack-icon'
import { SeriesAvatar } from 'pages/RepClubs/components/series-avatar'
import { useNotificationsContext } from 'contexts/notifications-context'

const timeAgo = new TimeAgo('en-US')

export interface NotificationDropdownProps {
	position?: string
}

const LIMIT = 10

export const EVENT_NOTIFICATION_TYPES = [
	NotificationType.EventCoHostInvitation,
	NotificationType.EventPromoteInvitation,
	NotificationType.EventSponsorInvitation,
	NotificationType.EventCoordinatorInvitation,
	NotificationType.EventTalentInvitation,
	NotificationType.EventPending,
	NotificationType.EventApproved,
	NotificationType.EventDeclined,
	NotificationType.EventInvitation,
	NotificationType.EventStarted,
	NotificationType.EventRoleUpdate,
	NotificationType.EventCheckinSuccess,
	NotificationType.EventLinkOffer,
	NotificationType.UserAcceptedEventRole,
	NotificationType.UserDeclinedEventRole,
	NotificationType.ContestWinnerSelected,
	NotificationType.EventPublishedToFollowers,
	NotificationType.EventTicketReceived,
]

export const OFFER_NOTIFICATION_TYPES = [NotificationType.ClubUserClaimedOffer, NotificationType.ClubUserEnteredOffer]

export const CLUB_NOTIFICATION_TYPES = [
	NotificationType.ClubOfferUserSelected,
	NotificationType.ClubOfferUserNotSelected,
	NotificationType.ClubTeamInviteAccepted,
]

const CONTACT_IN_PROGRESS_TYPES = [
	NotificationType.ContactImportStarted,
	NotificationType.ContactDeleteStarted,
	NotificationType.ContactInviteStarted,
	NotificationType.ContactMessageStarted,
]

const CONTACT_COMPLETED_TYPES = [
	NotificationType.ContactImportCompleted,
	NotificationType.ContactImportFailed,
	NotificationType.ContactDeleteCompleted,
	NotificationType.ContactDeleteFailed,
	NotificationType.ContactInviteCompleted,
	NotificationType.ContactInviteFailed,
	NotificationType.ContactMessageCompleted,
	NotificationType.ContactMessageFailed,
]

// Avatar component with verified badge
const VerifiedAvatar = ({
	src,
	isVerified,
	tier,
	className = 'h-11 w-11 rounded-full object-cover',
}: {
	src?: string
	isVerified?: boolean
	tier?: string
	className?: string
}) => {
	if (!src) return null

	return (
		<div className="relative">
			<AvatarImage className={className} src={src} />
			{isVerified && (
				<div className="absolute bottom-0 right-0">
					<IconSvg
						icon="verified"
						className={clsx('text-accent-text', {
							'!text-yellow-400': UserTier.Premium === tier,
						})}
					/>
				</div>
			)}
		</div>
	)
}

// Notification avatar renderer
const NotificationAvatar = ({ item }: { item: NotificationItem }) => {
	const notificationType = item.content?.defaultPayload?.notificationType as NotificationType | undefined
	const config = notificationType ? NOTIFICATION_CONFIG[notificationType] : undefined

	// The series' image, or the Loyalink logo when it has none (or the invite predates series images).
	if (notificationType === NotificationType.EventSeriesRoleInvitation) {
		return (
			<SeriesAvatar
				avatarUrl={getCustomProperty(item, 'SeriesAvatar')}
				className="h-11 w-11 bg-white-25"
				fallback={<img className="w-11 h-11 rounded-2xl overflow-hidden" src="/images/logos/logo-badge.svg" alt="" />}
			/>
		)
	}

	if (!config) {
		// Handle event and offer notifications
		if (notificationType && EVENT_NOTIFICATION_TYPES.includes(notificationType as NotificationType)) {
			const eventAvatar = getNotificationEventAvatar(item)
			// No avatar: the event is a series' hidden parent event (every real event has one).
			if (!eventAvatar) {
				return (
					<div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-lg bg-white-25">
						<div className="scale-75">
							<SeriesStackIcon />
						</div>
					</div>
				)
			}
			return (
				<AvatarImage
					className="h-11 w-11 rounded-lg object-cover overflow-hidden bg-white-25"
					src={eventAvatar}
				/>
			)
		}
		if (notificationType && OFFER_NOTIFICATION_TYPES.includes(notificationType as NotificationType)) {
			return (
				<AvatarImage
					className="h-11 w-11 rounded-lg object-cover overflow-hidden bg-white-25"
					src={getCustomProperty(item, 'OfferImageUrl')}
				/>
			)
		}
		if (notificationType && CLUB_NOTIFICATION_TYPES.includes(notificationType as NotificationType)) {
			return (
				<AvatarImage
					className="h-11 w-11 rounded-lg object-cover overflow-hidden bg-white-25"
					src={getCustomProperty(item, 'ClubImageUrl') || getCustomProperty(item, 'InvitorAvatar')}
				/>
			)
		}
		return null
	}

	// Handle image-based notifications
	if ('imageSrc' in config && config.imageSrc) {
		return <img className="w-11 h-11 rounded-2xl overflow-hidden" src={config.imageSrc} alt="" />
	}

	// Handle avatar-based notifications
	if ('avatarKey' in config && config.avatarKey) {
		const avatarSrc = getCustomProperty(item, config.avatarKey)

		if (!avatarSrc)
			return <img className="w-11 h-11 rounded-2xl overflow-hidden" src="/images/logos/logo-badge.svg" alt="" />

		// Pie images
		if (
			notificationType &&
			[
				NotificationType.CreatePie,
				NotificationType.NewWisherAdded,
				NotificationType.WishedPieActivated,
				NotificationType.BuySlices,
				NotificationType.PieOwnershipTransferRequest,
				NotificationType.PieOwnershipTransferAccepted,
				NotificationType.PieOwnershipTransferRejected,
			].includes(notificationType)
		) {
			return <PieImage className="w-11 h-11 rounded-lg overflow-hidden" images={[avatarSrc]} />
		}

		// Achievement/Reward avatars
		if (
			notificationType &&
			([NotificationType.ReceiveAchievement].includes(notificationType) ||
				[
					NotificationType.ReceiveReward,
					NotificationType.GenerateRewardWinnersReminder,
					NotificationType.AwardedReward,
					NotificationType.RewardLoser,
				].includes(notificationType))
		) {
			return (
				<AvatarImage
					className="h-11 w-11 rounded-lg object-cover overflow-hidden bg-white-25"
					src={avatarSrc}
				/>
			)
		}

		// User avatars with verified badge
		if ('verifiedKey' in config && config.verifiedKey) {
			return (
				<VerifiedAvatar
					src={avatarSrc}
					isVerified={getCustomProperty(item, config.verifiedKey) === 'true'}
					tier={config.tierKey ? getCustomProperty(item, config.tierKey) : undefined}
				/>
			)
		}

		return <AvatarImage className="h-11 w-11 rounded-lg object-cover overflow-hidden bg-white-25" src={avatarSrc} />
	}

	return null
}

// Notification title renderer
const NotificationTitle = ({ item }: { item: NotificationItem }) => {
	const notificationType = item.content?.defaultPayload?.notificationType as NotificationType | undefined
	const config = notificationType ? NOTIFICATION_CONFIG[notificationType] : undefined

	if (!config) {
		if (notificationType && EVENT_NOTIFICATION_TYPES.includes(notificationType as NotificationType)) {
			return <div className="font-bold">{getCustomProperty(item, 'EventName') || 'Event'}</div>
		}
		if (notificationType && OFFER_NOTIFICATION_TYPES.includes(notificationType as NotificationType)) {
			return <div className="font-bold">{getCustomProperty(item, 'OfferTitle') || 'Offer'}</div>
		}
		if (notificationType && CLUB_NOTIFICATION_TYPES.includes(notificationType as NotificationType)) {
			return <div className="font-bold">{getCustomProperty(item, 'ClubName') || 'Club'}</div>
		}
		return null
	}

	if ('title' in config && config.title) {
		return <div className="font-bold">{config.title}</div>
	}

	if ('titleKey' in config && config.titleKey) {
		const titleValue = getCustomProperty(item, config.titleKey)
		return titleValue ? <div className="font-bold">{titleValue}</div> : null
	}

	return null
}

export function NotificationDropdown({ position }: NotificationDropdownProps) {
	const { token } = useAuth()
	const { user, setTransferringPieInfo } = useStore()
	const navigate = useNavigate()
	const [notifications, setNotifications] = useState<NotificationItem[]>([])
	const [countUnreadNotifications, setCountUnreadNotifications] = useState<number>(0)
	const [readTimeoutId, setReadTimeoutId] = useState<NodeJS.Timeout | null>(null)
	const [endCursor, setEndCursor] = useState<string | null>(null)
	const [hasNextPage, setHasNextPage] = useState(false)

	const { latestWsNotification } = useNotificationsContext()

	const { loading, refetch } = useGetNotificationsQuery({
		variables: {
			first: LIMIT,
		},
		skip: !token || !user,
		notifyOnNetworkStatusChange: true,
		fetchPolicy: 'no-cache',
		onCompleted: data => {
			const nodes = data.notification?.listNotifications2?.nodes ?? []
			setNotifications(prev => (endCursor ? [...prev, ...nodes] : nodes))
			setCountUnreadNotifications(Math.max(0, data.notification?.countUnreadNotifications ?? 0))
			setEndCursor(data.notification?.listNotifications2?.pageInfo?.endCursor ?? null)
			setHasNextPage(data.notification?.listNotifications2?.pageInfo?.hasNextPage ?? false)
		},
	})

	useEffect(() => {
		if (!latestWsNotification) return
		setNotifications(prev => {
			const ids = new Set(prev.map(n => n.notificationId))
			if (ids.has(latestWsNotification.notificationId)) return prev
			return [latestWsNotification, ...prev]
		})
		if (!latestWsNotification.isRead) setCountUnreadNotifications(prev => prev + 1)
	}, [latestWsNotification])

	const [readNotification, { loading: readNotificationLoading }] = useReadNotificationMutation({})
	const [readAllNotification, { loading: readAllNotificationLoading }] = useReadAllNotificationMutation({
		onCompleted: () => {
			setNotifications(prev => prev.map(item => ({ ...item, isRead: true })))
			setCountUnreadNotifications(0)
		},
	})

	const handleNotificationRead = useCallback(
		(item: NotificationItem) => {
			if (item.isRead) return

			readNotification({
				variables: { id: item.notificationId },
			})

			setNotifications(prev =>
				prev.map(nItem => (nItem.notificationId === item.notificationId ? { ...nItem, isRead: true } : nItem))
			)

			setCountUnreadNotifications(prev => Math.max(0, prev - 1))
		},
		[readNotification]
	)

	const handleMarkAllAsRead = useCallback(() => {
		readAllNotification()
		setCountUnreadNotifications(0)
	}, [readAllNotification])

	const handleNotificationClick = useCallback(
		(item: NotificationItem) => {
			handleNotificationRead(item)
			const notifType = item.content?.defaultPayload?.notificationType as NotificationType | undefined
			const jobId = getCustomProperty(item, 'ImportJobId')

			// Invite operations — navigate to event invite page with jobId + status modal
			const isInvite =
				notifType &&
				[
					NotificationType.ContactInviteStarted,
					NotificationType.ContactInviteCompleted,
					NotificationType.ContactInviteFailed,
				].includes(notifType)
			if (isInvite && jobId) {
				const eventSlug = getCustomProperty(item, 'EventSlug')
				navigate(`/manage/rep-clubs/events/${eventSlug}/invite?inviteJobId=${jobId}`)
				return
			}

			// Contact import/delete/message operations — navigate to contacts page
			// with the job-kind specific query param so the right status modal opens.
			if (notifType && [...CONTACT_IN_PROGRESS_TYPES, ...CONTACT_COMPLETED_TYPES].includes(notifType) && jobId) {
				const isDelete = [
					NotificationType.ContactDeleteStarted,
					NotificationType.ContactDeleteCompleted,
					NotificationType.ContactDeleteFailed,
				].includes(notifType)
				const isMessage = [
					NotificationType.ContactMessageStarted,
					NotificationType.ContactMessageCompleted,
					NotificationType.ContactMessageFailed,
				].includes(notifType)
				const paramName = isDelete ? 'deleteJobId' : isMessage ? 'messageJobId' : 'importJobId'
				navigate(`/manage/rep-clubs/contacts?${paramName}=${jobId}`)
				return
			}

			const url = getNotificationNavigation(item, setTransferringPieInfo)
			navigate(url)
		},
		[handleNotificationRead, navigate, setTransferringPieInfo]
	)

	const handleMouseEnter = useCallback(
		(item: NotificationItem) => {
			if (readNotificationLoading || item.isRead) return

			const timeoutId = setTimeout(() => {
				handleNotificationRead(item)
			}, 2000)

			setReadTimeoutId(timeoutId)
		},
		[handleNotificationRead, readNotificationLoading]
	)

	const handleMouseLeave = useCallback(() => {
		if (readTimeoutId) {
			clearTimeout(readTimeoutId)
			setReadTimeoutId(null)
		}
	}, [readTimeoutId])

	return (
		<div>
			<Menu as="div" className="relative text-left">
				<div className="flex items-center">
					<MenuButton className="relative" aria-label="Notifications">
						<IconSvg icon="notifications" width={24} height={24} />
						{countUnreadNotifications > 0 && (
							<span
								className="absolute top-0 right-0 size-1.5 rounded-full bg-red shrink-0"
								aria-label={`${countUnreadNotifications} unread notifications`}
							/>
						)}
					</MenuButton>
				</div>

				<Transition
					as={Fragment}
					enter="transition ease-out duration-100"
					enterFrom="transform opacity-0 scale-95"
					enterTo="transform opacity-100 scale-100"
					leave="transition ease-in duration-75"
					leaveFrom="transform opacity-100 scale-100"
					leaveTo="transform opacity-0 scale-95"
				>
					<MenuItems
						className={clsx(
							'fixed inset-0 h-[100dvh] sm:absolute sm:inset-auto sm:h-auto lg:-right-2 sm:-right-14 rounded-none sm:rounded-lg shadow-lg bg-primary-surface/[0.4] backdrop-blur-lg focus:outline-none sm:mt-5 px-1 pt-[max(0px,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:px-1 sm:pb-6 lg:w-[573px] sm:w-[350px] z-50 sm:z-10 overflow-hidden flex flex-col border border-translucent-plus-border',
							{
								'origin-top-right': position !== 'top',
								'origin-bottom-right bottom-full': position === 'top',
							}
						)}
					>
						<div className="flex items-center justify-between px-3 py-4 border-b border-translucent-plus-border flex-shrink-0">
							<MenuItem>
								<Link to="/profile/notifications">
									<Button variant="link">View all</Button>
								</Link>
							</MenuItem>
							<div className="flex items-center gap-8">
								<Button
									variant="link"
									onClick={handleMarkAllAsRead}
									disabled={readAllNotificationLoading || countUnreadNotifications === 0}
								>
									Mark all as read
								</Button>
								<MenuItem>
									{({ close }) => (
										<button
											type="button"
											onMouseDown={e => {
												e.preventDefault()
												e.stopPropagation()
											}}
											onClick={e => {
												e.preventDefault()
												e.stopPropagation()
												// Use setTimeout to ensure close happens after event cycle
												setTimeout(() => {
													close()
												}, 0)
											}}
											className="sm:hidden flex items-center justify-center w-8 h-8 rounded-full hover:bg-white/5 transition-colors"
											aria-label="Close notifications"
										>
											<IconSvg icon="close" />
										</button>
									)}
								</MenuItem>
							</div>
						</div>
						<div
							id="notification-scroll-container"
							className="app-scrollbar flex-1 overflow-auto py-2 px-1 lg:max-h-[700px] sm:max-h-[500px]"
						>
							{loading && notifications.length === 0 ? (
								<div className="flex justify-center py-6">
									<FontAwesomeIcon icon={['fas', 'spinner']} size="lg" spin className="text-white" />
								</div>
							) : notifications.length === 0 ? (
								<div className="text-center py-6 text-secondary-text text-sm">No notifications</div>
							) : (
								<InfiniteScroll
									dataLength={notifications.length}
									next={() => refetch({ first: LIMIT, after: endCursor })}
									hasMore={hasNextPage}
									loader={
										<div className="flex justify-center py-3">
											<FontAwesomeIcon
												icon={['fas', 'spinner']}
												size="sm"
												spin
												className="text-white"
											/>
										</div>
									}
									scrollableTarget="notification-scroll-container"
									className="!overflow-visible"
								>
									{notifications.map(item => (
										<MenuItem key={item.notificationId} as={Fragment}>
											{() => (
												<button
													type="button"
													className="block p-3 rounded-lg hover:bg-tint-plus-surface w-full relative transition-all duration-300 text-primary-text"
													onClick={() => handleNotificationClick(item)}
													onMouseEnter={() => handleMouseEnter(item)}
													onMouseLeave={handleMouseLeave}
													aria-label={`Notification: ${item.content.defaultMessage}`}
												>
													<div className="flex space-x-3 items-top pr-10">
														<div className="flex-shrink-0">
															<NotificationAvatar item={item} />
														</div>
														<div className="text-left">
															<NotificationTitle item={item} />
															<div className="text-sm leading-[18px] text-left font-normal mt-1">
																{item.content.defaultMessage}
															</div>
															<div className="text-xs leading-[18px] font-normal mt-1 text-secondary-text">
																{timeAgo.format(new Date(item.createdAt))}
															</div>
														</div>
													</div>
													{!item.isRead && (
														<button
															type="button"
															className="w-12 h-full absolute right-0 top-0"
															onClick={e => {
																e.stopPropagation()
																handleNotificationRead(item)
															}}
															disabled={readNotificationLoading}
															aria-label="Mark as read"
														>
															<span className="w-3 h-3 bg-accent-surface/70 rounded-full absolute right-6 top-1/2 -mt-1.5 hover:bg-accent-surface transition-all duration-300" />
														</button>
													)}
												</button>
											)}
										</MenuItem>
									))}
								</InfiniteScroll>
							)}
						</div>
					</MenuItems>
				</Transition>
			</Menu>
		</div>
	)
}
